import React, {Dispatch, SetStateAction, useState} from "react";
import {useTranslation} from "react-i18next";
import {Modal} from "@awsui/components-react";
import Header from "@awsui/components-react/header";
import Box from "@awsui/components-react/box";
import CreateNewLeagueButton from "pages/leagues/components/CreateNewLeagueButton";
import NewLeague from "model/NewLeague";
import {FlashbarProps} from "@awsui/components-react/flashbar/interfaces";
import SubmitFlashbar from "components/SubmitFlashbar";
import SpaceBetween from "@awsui/components-react/space-between";
import Form from "@awsui/components-react/form";
import Button from "@awsui/components-react/button";
import FormField from "@awsui/components-react/form-field";
import Input from "@awsui/components-react/input";
import Select from "@awsui/components-react/select";
import League from "model/League";


interface NewLeaguesModalProps {
    showModal: boolean;
    setShowModal: Dispatch<SetStateAction<boolean>>;
    leagues?: League[];
    setLeagues: Dispatch<SetStateAction<League[] | undefined>>;
}

enum LeaguePrivacy {
    PRIVATE = "PRIVATE",
    PUBLIC = "PUBLIC"
}

const NewLeaguesModal: React.FC<NewLeaguesModalProps> = (props) => {

    const {t} = useTranslation(["leagues", "common"]);
    const [newLeague, setNewLeague] = useState<NewLeague>({});
    const [submitMessage, setSubmitMessage] = useState<FlashbarProps.MessageDefinition>();
    const [buttonEnabled, setButtonEnabled] = useState(true);

    const dismissModal = () => {
        props.setShowModal(false);
        setSubmitMessage(undefined);
    };

    const changeLeague = (newLeague: NewLeague): void => {
        setNewLeague(newLeague);
        setButtonEnabled(true);
    };

    return (
        <Modal
            visible={props.showModal}
            onDismiss={dismissModal}
            header={<Header variant={"h2"}>{t("leagues:newLeagueModal.title")}</Header>}
        >
            <SpaceBetween size={"s"}>
                <SpaceBetween size={"m"}>
                    {submitMessage && (
                        <SubmitFlashbar
                            submitMessage={submitMessage}
                            setSubmitMessage={setSubmitMessage}
                        />
                    )}
                    <form onSubmit={e => e.preventDefault()}>
                        <Form
                            actions={
                                <Box textAlign={"right"}>
                                    <SpaceBetween direction="horizontal" size="xs">
                                        <Button formAction="none" variant="normal" onClick={dismissModal}>
                                            {t("common:buttons.close")}
                                        </Button>
                                        <CreateNewLeagueButton
                                            newLeague={newLeague}
                                            setShowModal={props.setShowModal}
                                            buttonEnabled={buttonEnabled}
                                            setButtonEnabled={setButtonEnabled}
                                            setSubmitMessage={setSubmitMessage}
                                            leagues={props.leagues}
                                            setLeagues={props.setLeagues}
                                        />
                                    </SpaceBetween>
                                </Box>
                            }
                        >
                            <SpaceBetween direction="vertical" size="l">
                                <FormField key={"name"} label={<Header variant="h2">{t("leagues:newLeagueModal.nameLabel")}</Header>}>
                                    <Input
                                        inputMode={"text"}
                                        onChange={({detail}) => changeLeague({
                                            ...newLeague,
                                            name: detail.value
                                        })}
                                        value={newLeague?.name || ""}
                                    />
                                </FormField>
                                <FormField key={"isPrivate"} label={<Header variant="h2">{t("leagues:newLeagueModal.privacyLabel")}</Header>}>
                                    <Select
                                        selectedOption={newLeague.isPrivate ? {
                                            label: t("leagues:newLeagueModal.private"),
                                            value: LeaguePrivacy.PRIVATE
                                        } : {
                                            label: t("leagues:newLeagueModal.public"),
                                            value: LeaguePrivacy.PUBLIC
                                        }}
                                        onChange={({detail}) => changeLeague({
                                            ...newLeague,
                                            isPrivate: detail.selectedOption.value === LeaguePrivacy.PRIVATE
                                        })}
                                        options={[
                                            {
                                                label: t("leagues:newLeagueModal.public"),
                                                value: LeaguePrivacy.PUBLIC
                                            },
                                            {
                                                label: t("leagues:newLeagueModal.private"),
                                                value: LeaguePrivacy.PRIVATE
                                            }
                                        ]}
                                    />
                                </FormField>
                                {newLeague.isPrivate && (
                                    <FormField key={"password"} label={<Header variant="h2">{t("leagues:newLeagueModal.passwordLabel")}</Header>}>
                                        <Input
                                            inputMode={"text"}
                                            type={"password"}
                                            onChange={({detail}) => changeLeague({
                                                ...newLeague,
                                                password: detail.value
                                            })}
                                            value={newLeague?.password || ""}
                                            disableBrowserAutocorrect
                                        />
                                    </FormField>
                                )}
                            </SpaceBetween>
                        </Form>
                    </form>
                </SpaceBetween>
            </SpaceBetween>
        </Modal>
    );
};

export default NewLeaguesModal;
